import { useEffect, useState, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import AuthPage from "@/components/auth/AuthPage";
import HubPortal from "@/components/hub/HubPortal";
import Lobby from "@/components/lobby/Lobby";
import LandingPage from "@/components/landing/LandingPage";
import PlannerDashboard from "@/components/planner/PlannerDashboard";
import DriverDashboard from "@/components/driver/DriverDashboard";
import MachinistDashboard from "@/components/machinist/MachinistDashboard";
import ServiceDashboard from "@/components/service/ServiceDashboard";
import TransportDashboard from "@/components/transport/TransportDashboard";
import ViewerDashboard from "@/components/shared/ViewerDashboard";

import InstallApp from "@/components/install/InstallApp";

export const AppContext = createContext(null);

export function useAppContext() {
  return useContext(AppContext);
}

function AuthRedirect({ isAuthenticated }) {
  const [searchParams] = useSearchParams();
  const invite = searchParams.get("invite");
  if (isAuthenticated) {
    return invite ? <Navigate to={`/join?invite=${invite}`} /> : <Navigate to="/portal" />;
  }
  return <AuthPage />;
}

function App() {
  const [auth, setAuth] = useState(() => {
    const saved = localStorage.getItem("pigeon-auth");
    return saved ? JSON.parse(saved) : null;
  });

  const [session, setSession] = useState(() => {
    const saved = localStorage.getItem("pigeon-session");
    return saved ? JSON.parse(saved) : null;
  });

  const [hubData, setHubData] = useState({ sites: [], machines: [], drivers: [], requests: [], events: [], messages: [], feedback: [] });
  const [loading, setLoading] = useState(false);

  // Dynamic HTML lang attribute for SEO & accessibility
  useEffect(() => {
    const lang = localStorage.getItem("pigeon-lang") || "en";
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, []);

  useEffect(() => {
    if (auth) {
      localStorage.setItem("pigeon-auth", JSON.stringify(auth));
      // Sync user language preference from DB
      if (auth.user?.language) {
        localStorage.setItem("pigeon-lang", auth.user.language);
        document.documentElement.lang = auth.user.language;
        document.documentElement.dir = auth.user.language === "ar" ? "rtl" : "ltr";
      }
    } else {
      localStorage.removeItem("pigeon-auth");
    }
  }, [auth]);

  useEffect(() => {
    if (session) localStorage.setItem("pigeon-session", JSON.stringify(session));
    else localStorage.removeItem("pigeon-session");
  }, [session]);

  const refreshData = async () => {
    if (!session?.hubId) return;
    setLoading(true);
    try {
      const [sites, machines, drivers, requests, events, messages, feedback] = await Promise.all([
        api.getSites(session.hubId),
        api.getMachines(session.hubId),
        api.getDrivers(session.hubId),
        api.getRequests(session.hubId),
        api.getEvents(session.hubId, 100),
        api.getMessages(session.hubId),
        api.getFeedback(session.hubId),
      ]);
      setHubData({ sites, machines, drivers, requests, events, messages, feedback });
    } catch (e) {
      console.error("Data refresh error:", e);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (session?.hubId) {
      refreshData();
      const interval = setInterval(refreshData, 15000);
      return () => clearInterval(interval);
    }
  }, [session?.hubId]);

  const logout = () => {
    setAuth(null);
    setSession(null);
    localStorage.removeItem("pigeon-auth");
    localStorage.removeItem("pigeon-session");
  };

  const ctxValue = {
    auth, setAuth, session, setSession, hubData, refreshData, loading, logout, setHubData
  };

  const isAuthenticated = !!auth?.jwt;

  const canAccessDashboard = (targetRole) => {
    if (!session?.role) return false;
    if (session.role === targetRole) return true;
    const roleConfig = session?.asset_config?.role_config || "full";
    if (roleConfig === "solo") return true; // Solo can access all dashboards
    if (roleConfig === "planner_driver") {
      return ["planner", "driver", "transport", "viewer"].includes(targetRole);
    }
    if (roleConfig === "planner_machinist") {
      return ["planner", "machinist", "service", "viewer"].includes(targetRole);
    }
    return false; // Full crew — stay in your lane
  };

  return (
    <AppContext.Provider value={ctxValue}>
      <div className="App">
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route path="/landing" element={<LandingPage />} />
            <Route path="/success" element={<LandingPage />} />
            <Route path="/auth" element={<AuthRedirect isAuthenticated={isAuthenticated} />} />

            <Route path="/install" element={<InstallApp />} />

            {/* Auth-protected routes */}
            <Route path="/portal" element={isAuthenticated ? <HubPortal /> : <Navigate to="/auth" />} />
            <Route path="/join" element={isAuthenticated ? <HubPortal /> : <AuthPage />} />

            {/* Dashboard routes - need auth + session */}
            {/* Role-switching allowed for solo/planner_driver/planner_machinist configs */}
            <Route path="/planner" element={canAccessDashboard("planner") ? <PlannerDashboard /> : <Navigate to={isAuthenticated ? "/portal" : "/auth"} />} />
            <Route path="/driver/:driverName" element={canAccessDashboard("driver") ? <DriverDashboard /> : <Navigate to={isAuthenticated ? "/portal" : "/auth"} />} />
            <Route path="/driver" element={canAccessDashboard("driver") ? <DriverDashboard /> : <Navigate to={isAuthenticated ? "/portal" : "/auth"} />} />
            <Route path="/machinist" element={canAccessDashboard("machinist") ? <MachinistDashboard /> : <Navigate to={isAuthenticated ? "/portal" : "/auth"} />} />
            <Route path="/service" element={canAccessDashboard("service") ? <ServiceDashboard /> : <Navigate to={isAuthenticated ? "/portal" : "/auth"} />} />
            <Route path="/transport" element={canAccessDashboard("transport") ? <TransportDashboard /> : <Navigate to={isAuthenticated ? "/portal" : "/auth"} />} />
            <Route path="/viewer" element={canAccessDashboard("viewer") ? <ViewerDashboard /> : <Navigate to={isAuthenticated ? "/portal" : "/auth"} />} />

            {/* Legacy lobby for demo hub access */}
            <Route path="/demo" element={<Lobby />} />

            {/* Root redirects to auth or portal */}
            <Route path="/" element={isAuthenticated ? <Navigate to="/portal" /> : <Navigate to="/landing" />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </BrowserRouter>
      </div>
    </AppContext.Provider>
  );
}

export default App;
